import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Download, Sparkles, Loader2, Copy, Check, X, Wand2, Eye, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DraftingModule = ({ caseId, caseData, onClose }) => {
  const { t } = useTranslation('drafting');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [editedDraft, setEditedDraft] = useState('');
  const [copied, setCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/drafting/templates`);
      setTemplates(Object.entries(response.data.templates).map(([key, value]) => ({
        id: key,
        ...(value as object)
      })));
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const generateDraft = async () => {
    if (!selectedTemplate) {
      toast.error(t('toast.selectTemplate'));
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/drafting/generate`, {
        case_id: caseId,
        template_type: selectedTemplate,
        context: {
          client_name: 'Klient',
          case_topic: caseData.topic,
          case_summary: caseData.intake_summary
        }
      });

      setDraft(response.data);
      setEditedDraft(response.data.draft);
      toast.success(t('toast.draftGenerated'));
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(t('toast.generateFailed'));
    } finally {
      setGenerating(false);
    }
  };

  const downloadDocx = async () => {
    if (!draft) return;

    try {
      const response = await axios.get(`${API}/drafting/export/${draft.draft_id}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `draft_${draft.draft_id.substring(0, 8)}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(t('toast.docxDownloaded'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('toast.downloadFailed'));
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(editedDraft);
    setCopied(true);
    toast.success(t('toast.copied'));
    setTimeout(() => setCopied(false), 2000);
  };

  const wordCount = editedDraft.split(/\s+/).filter(w => w.length > 0).length;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      
      {/* Fullscreen Modal */}
      <div className="fixed inset-0 md:inset-8 z-50 bg-white md:rounded-2xl shadow-2xl flex flex-col">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white md:rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-gray-900 to-gray-700 rounded-xl flex items-center justify-center shadow-md">
              <Wand2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('title')}</h2>
              <p className="text-sm text-gray-600">{t('subtitle')}</p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {!draft ? (
          /* Template Selection */
          <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50">
            
            {/* Case Context */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-gray-600" />
                <h3 className="text-base font-bold text-gray-900">{t('caseInfo.title')}</h3>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{caseData.topic}</h4>
              <p className="text-sm text-gray-600 leading-relaxed">{caseData.intake_summary}</p>
            </div>

            {/* Templates Grid */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">{t('templates.selectTitle')}</h3>
              <div className="grid gap-4">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={`text-left p-5 rounded-2xl border-2 transition-all ${
                      selectedTemplate === template.id
                        ? 'border-gray-900 bg-gray-900 text-white shadow-lg'
                        : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md ${
                        selectedTemplate === template.id ? 'bg-white' : 'bg-gray-100'
                      }`}>
                        <FileText className={`w-6 h-6 ${
                          selectedTemplate === template.id ? 'text-gray-900' : 'text-gray-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-bold mb-1 ${
                          selectedTemplate === template.id ? 'text-white' : 'text-gray-900'
                        }`}>
                          {template.name}
                        </h3>
                        <p className={`text-sm mb-3 ${
                          selectedTemplate === template.id ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          {template.description}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {template.sections.slice(0, 4).map((section, i) => (
                            <span
                              key={i}
                              className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                                selectedTemplate === template.id
                                  ? 'bg-white/20 text-white'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {section}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={generateDraft}
              disabled={!selectedTemplate || generating}
              className="w-full px-8 py-4 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('generate.generating')}
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  {t('generate.button')}
                </>
              )}
            </button>

            {!selectedTemplate && (
              <p className="text-sm text-gray-500 text-center italic">
                {t('templates.selectPrompt')}
              </p>
            )}
          </div>
        ) : (
          /* Draft Editor */
          <div className="flex-1 flex flex-col">
            {/* Editor Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="text-base font-bold text-gray-900">{t('editor.title')}</h3>
                <p className="text-sm text-gray-600">
                  {draft.template_type} • {t('editor.wordCount', { count: wordCount })}
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    showPreview
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Eye className="w-3.5 h-3.5 inline mr-1" />
                  {showPreview ? t('editor.edit') : t('editor.preview')}
                </button>
                <button
                  onClick={copyToClipboard}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium transition-colors"
                >
                  {copied ? (
                    <><Check className="w-3.5 h-3.5 inline mr-1" />{t('actions.copied')}</>
                  ) : (
                    <><Copy className="w-3.5 h-3.5 inline mr-1" />{t('actions.copy')}</>
                  )}
                </button>
                <button
                  onClick={downloadDocx}
                  className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-md hover:shadow-lg transition-all"
                >
                  <Download className="w-3.5 h-3.5 inline mr-1" />
                  {t('actions.downloadDocx')}
                </button>
              </div>
            </div>

            {/* Editor/Preview */}
            <div className="flex-1 p-6 overflow-y-auto bg-white">
              {showPreview ? (
                <div className="max-w-4xl mx-auto prose prose-sm">
                  <div className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
                    {editedDraft}
                  </div>
                </div>
              ) : (
                <textarea
                  value={editedDraft}
                  onChange={(e) => setEditedDraft(e.target.value)}
                  className="w-full h-full min-h-[600px] p-6 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm leading-relaxed resize-none"
                  placeholder={t('editor.placeholder')}
                />
              )}
            </div>

            {/* Footer Actions */}
            <div className="border-t border-gray-200 p-6 bg-white flex gap-3">
              <button
                onClick={() => {
                  setDraft(null);
                  setSelectedTemplate(null);
                  setEditedDraft('');
                }}
                className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold rounded-xl transition-colors inline-flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                {t('actions.newTemplate')}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DraftingModule;
